import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';

const ForgotPasswordSection = () => {
    const navigate = useNavigate();

    // Steps: 1 = Email Input, 2 = OTP Verification, 3 = New Password, 4 = Success
    const [step, setStep] = useState(1);
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [globalError, setGlobalError] = useState('');

    // Step 2: OTP State
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [otpError, setOtpError] = useState('');
    const [resendSeconds, setResendSeconds] = useState(60);
    const [isResending, setIsResending] = useState(false);
    const otpRefs = useRef([]);

    // Step 3: Password State
    const [passwords, setPasswords] = useState({ newPassword: '', confirmPassword: '' });
    const [passwordErrors, setPasswordErrors] = useState({});
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Step 4: Auto-redirect countdown
    const [redirectCountdown, setRedirectCountdown] = useState(3);

    // ── Resend Countdown Timer (1 minute cooldown) ───────────────────────────
    useEffect(() => {
        let interval = null;
        if (step === 2 && resendSeconds > 0) {
            interval = setInterval(() => {
                setResendSeconds((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [step, resendSeconds]);

    // ── Auto focus on first OTP box when entering Step 2 ─────────────────────
    useEffect(() => {
        if (step === 2 && otpRefs.current[0]) {
            setTimeout(() => {
                otpRefs.current[0]?.focus();
            }, 150);
        }
    }, [step]);

    // ── Step 4 Redirect Countdown ────────────────────────────────────────────
    useEffect(() => {
        let redirectTimer = null;
        if (step === 4) {
            if (redirectCountdown > 0) {
                redirectTimer = setTimeout(() => {
                    setRedirectCountdown((prev) => prev - 1);
                }, 1000);
            } else {
                navigate('/login');
            }
        }
        return () => clearTimeout(redirectTimer);
    }, [step, redirectCountdown, navigate]);

    const validateEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());

    // ── STEP 1: Submit Email (Verify presence in DB & Send OTP) ──────────────
    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        setGlobalError('');
        setEmailError('');

        if (!email.trim()) {
            setEmailError('Please enter your registered email address.');
            return;
        }
        if (!validateEmail(email)) {
            setEmailError('Please enter a valid email address.');
            return;
        }

        setIsSubmitting(true);
        try {
            const res = await api.post('/api/auth/forgot-password', {
                email: email.trim().toLowerCase(),
            });

            if (res.data?.success || res.status === 200) {
                toast.success('Verification code sent! Please check your inbox.');
                setStep(2);
                setResendSeconds(60); // 1 minute cooldown begins
                setOtp(['', '', '', '', '', '']);
                setOtpError('');
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Unable to process request. Please try again.';
            setGlobalError(msg);
            toast.error(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── STEP 2: OTP Input Handlers ───────────────────────────────────────────
    const handleOtpChange = (index, e) => {
        const val = e.target.value;
        setOtpError('');
        setGlobalError('');

        // Handle numeric digit input
        const digit = val.replace(/\D/g, '').slice(-1);
        const newOtp = [...otp];
        newOtp[index] = digit;
        setOtp(newOtp);

        // Auto-advance to next input
        if (digit && index < 5) {
            otpRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace') {
            if (!otp[index] && index > 0) {
                const newOtp = [...otp];
                newOtp[index - 1] = '';
                setOtp(newOtp);
                otpRefs.current[index - 1]?.focus();
            } else {
                const newOtp = [...otp];
                newOtp[index] = '';
                setOtp(newOtp);
            }
        } else if (e.key === 'ArrowLeft' && index > 0) {
            otpRefs.current[index - 1]?.focus();
        } else if (e.key === 'ArrowRight' && index < 5) {
            otpRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpPaste = (e) => {
        e.preventDefault();
        setOtpError('');
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        if (!pastedData) return;

        const newOtp = [...otp];
        for (let i = 0; i < 6; i++) {
            newOtp[i] = pastedData[i] || '';
        }
        setOtp(newOtp);

        const nextFocusIndex = Math.min(pastedData.length, 5);
        otpRefs.current[nextFocusIndex]?.focus();
    };

    // ── STEP 2: Verify OTP ───────────────────────────────────────────────────
    const handleOtpSubmit = async (e) => {
        e.preventDefault();
        setOtpError('');
        setGlobalError('');

        const otpCode = otp.join('');
        if (otpCode.length !== 6) {
            setOtpError('Please enter the full 6-digit verification code.');
            return;
        }

        setIsSubmitting(true);
        try {
            const res = await api.post('/api/auth/verify-otp', {
                email: email.trim().toLowerCase(),
                otp: otpCode,
            });

            if (res.data?.success || res.status === 200) {
                toast.success('Code verified successfully!');
                setStep(3);
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Invalid or expired OTP. Please try again.';
            setOtpError(msg);
            setGlobalError(msg);
            toast.error(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── STEP 2: Resend OTP (Available after 1 minute) ─────────────────────────
    const handleResendOtp = async () => {
        if (resendSeconds > 0 || isResending) return;

        setIsResending(true);
        setGlobalError('');
        setOtpError('');
        try {
            const res = await api.post('/api/auth/resend-otp', {
                email: email.trim().toLowerCase(),
            });

            if (res.data?.success || res.status === 200) {
                toast.success('A new 6-digit code has been sent to your email!');
                setResendSeconds(60); // Reset 1-minute cooldown
                setOtp(['', '', '', '', '', '']);
                otpRefs.current[0]?.focus();
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Failed to resend code. Please try again.';
            setGlobalError(msg);
            toast.error(msg);
        } finally {
            setIsResending(false);
        }
    };

    // ── STEP 3: Submit New Password ──────────────────────────────────────────
    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setGlobalError('');
        setPasswordErrors({});

        const errors = {};
        if (!passwords.newPassword) {
            errors.newPassword = 'New password is required.';
        } else if (passwords.newPassword.length < 6) {
            errors.newPassword = 'Password must be at least 6 characters long.';
        }

        if (!passwords.confirmPassword) {
            errors.confirmPassword = 'Confirm your new password.';
        } else if (passwords.newPassword !== passwords.confirmPassword) {
            errors.confirmPassword = 'Passwords do not match.';
        }

        setPasswordErrors(errors);
        if (Object.keys(errors).length > 0) return;

        setIsSubmitting(true);
        try {
            const res = await api.post('/api/auth/reset-password', {
                email: email.trim().toLowerCase(),
                newPassword: passwords.newPassword,
                confirmPassword: passwords.confirmPassword,
            });

            if (res.data?.success || res.status === 200) {
                toast.success('Password updated successfully!');
                setStep(4);
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Failed to reset password. Please start over.';
            setGlobalError(msg);
            toast.error(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatSeconds = (sec) => {
        const mins = Math.floor(sec / 60);
        const remSecs = sec % 60;
        return mins + ':' + (remSecs < 10 ? '0' : '') + remSecs;
    };

    return (
        <section className="auth-section py-80 bg-gray-50 flex-center" style={{ minHeight: '680px', backgroundColor: '#fdfbfb' }}>
            <style>{`
                .forgot-card {
                    background: #ffffff;
                    border-radius: 20px;
                    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.06);
                    width: 100%;
                    max-width: 500px;
                    padding: 40px;
                    margin: 0 auto;
                    border: 1px solid #f1f1f1;
                    transition: all 0.3s ease;
                }
                .step-progress-bar {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 32px;
                    position: relative;
                }
                .step-progress-line {
                    position: absolute;
                    top: 18px;
                    left: 20px;
                    right: 20px;
                    height: 2px;
                    background: #ebebeb;
                    z-index: 1;
                }
                .step-progress-fill {
                    position: absolute;
                    top: 18px;
                    left: 20px;
                    height: 2px;
                    background: var(--main-600, #2FA84F);
                    z-index: 2;
                    transition: width 0.4s ease;
                }
                .step-item {
                    position: relative;
                    z-index: 3;
                    text-align: center;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }
                .step-circle {
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                    background: #f4f4f4;
                    color: #888;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 14px;
                    font-weight: 700;
                    border: 2px solid #e0e0e0;
                    transition: all 0.3s ease;
                }
                .step-item.active .step-circle {
                    background: var(--main-600, #2FA84F);
                    color: #fff;
                    border-color: var(--main-600, #2FA84F);
                    box-shadow: 0 0 0 4px rgba(47, 168, 79, 0.18);
                }
                .step-item.completed .step-circle {
                    background: #1b7e3d;
                    color: #fff;
                    border-color: #1b7e3d;
                }
                .step-label {
                    margin-top: 8px;
                    font-size: 12px;
                    font-weight: 600;
                    color: #888;
                }
                .step-item.active .step-label {
                    color: var(--main-600, #2FA84F);
                }
                .step-item.completed .step-label {
                    color: #1b7e3d;
                }
                .auth-input-group {
                    position: relative;
                    margin-bottom: 20px;
                }
                .auth-input-group i.left-icon {
                    position: absolute;
                    top: 15px;
                    left: 16px;
                    font-size: 20px;
                    color: var(--main-600, #2FA84F);
                }
                .auth-input-group i.right-icon {
                    position: absolute;
                    top: 15px;
                    right: 16px;
                    font-size: 20px;
                    color: #9AA0A6;
                    cursor: pointer;
                    transition: color 0.3s;
                }
                .auth-input-group i.right-icon:hover {
                    color: var(--main-600, #2FA84F);
                }
                .auth-input {
                    width: 100%;
                    border: 1px solid #EFEFEF;
                    border-radius: 12px;
                    padding: 14px 46px 14px 46px; 
                    font-size: 15px;
                    color: #1B1B1B;
                    outline: none;
                    background: #fafafa;
                    transition: border 0.3s, background 0.3s;
                }
                .auth-input:focus {
                    border-color: var(--main-600, #2FA84F);
                    background: #fff;
                }
                .auth-input-no-right {
                    padding-right: 16px;
                }
                .auth-error-msg {
                    color: #E0503A;
                    font-size: 12.5px;
                    margin-top: 6px;
                    margin-left: 10px;
                    display: flex;
                    align-items: center;
                    gap: 5px;
                }
                .auth-submit-btn {
                    width: 100%;
                    background: var(--main-600, #2FA84F);
                    color: #fff;
                    padding: 14px;
                    border: none;
                    border-radius: 12px;
                    font-size: 16px;
                    font-weight: 600;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    transition: all 0.3s;
                    margin-top: 16px;
                }
                .auth-submit-btn:hover:not(:disabled) {
                    background: var(--main-700, #268940);
                    transform: translateY(-1px);
                    box-shadow: 0 4px 14px rgba(47, 168, 79, 0.25);
                }
                .auth-submit-btn:disabled {
                    opacity: 0.65;
                    cursor: not-allowed;
                }
                .otp-box-container {
                    display: flex;
                    justify-content: center;
                    gap: 10px;
                    margin: 24px 0;
                }
                .otp-single-box {
                    width: 52px;
                    height: 56px;
                    font-size: 24px;
                    font-weight: 700;
                    text-align: center;
                    border: 2px solid #E4E7EC;
                    border-radius: 12px;
                    background: #F9FAFB;
                    color: #111827;
                    outline: none;
                    transition: all 0.2s ease;
                }
                .otp-single-box:focus {
                    border-color: var(--main-600, #2FA84F);
                    background: #ffffff;
                    box-shadow: 0 0 0 4px rgba(47, 168, 79, 0.15);
                }
                .otp-single-box.filled {
                    border-color: #2FA84F;
                    background: #F4FBF6;
                    color: #1b7e3d;
                }
                .otp-meta-row {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 10px;
                    margin-top: 18px;
                    padding: 12px 14px;
                    background: #f8faf9;
                    border-radius: 10px;
                    border: 1px solid #e9f3ec;
                }
                .otp-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 13px;
                    color: #4b5563;
                    font-weight: 500;
                }
                .otp-resend-btn {
                    background: none;
                    border: none;
                    color: var(--main-600, #2FA84F);
                    font-size: 13.5px;
                    font-weight: 600;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 0;
                    transition: color 0.2s;
                }
                .otp-resend-btn:hover:not(:disabled) {
                    color: var(--main-700, #268940);
                    text-decoration: underline;
                }
                .otp-resend-btn:disabled {
                    color: #9ca3af;
                    cursor: not-allowed;
                    text-decoration: none;
                }
                .global-alert {
                    padding: 14px 16px;
                    border-radius: 10px;
                    margin-bottom: 22px;
                    font-size: 14px;
                    display: flex;
                    align-items: flex-start;
                    gap: 10px;
                    line-height: 1.5;
                }
                .global-alert.error {
                    background: #FEF2F2;
                    color: #B91C1C;
                    border: 1px solid #FCA5A5;
                }
                .global-alert.success {
                    background: #F0FDF4;
                    color: #15803D;
                    border: 1px solid #BBF7D0;
                }
                .password-reqs {
                    font-size: 12.5px;
                    color: #6b7280;
                    margin-top: -10px;
                    margin-bottom: 16px;
                    padding-left: 4px;
                    list-style: none;
                }
                .password-reqs li {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    margin-top: 4px;
                }
                @media (max-width: 575px) {
                    .forgot-card {
                        padding: 24px 20px;
                    }
                    .otp-single-box {
                        width: 42px;
                        height: 48px;
                        font-size: 20px;
                        gap: 6px;
                    }
                    .step-label {
                        font-size: 11px;
                    }
                }
            `}</style>

            <div className="container">
                <div className="forgot-card wow zoomIn" data-wow-duration="0.6s">
                    
                    {/* Step Progress Bar */}
                    {step < 4 && (
                        <div className="step-progress-bar">
                            <div className="step-progress-line" />
                            <div
                                className="step-progress-fill"
                                style={{
                                    width: step === 1 ? '0%' : step === 2 ? '50%' : '100%',
                                }}
                            />
                            
                            <div className={'step-item ' + (step === 1 ? 'active' : step > 1 ? 'completed' : '')}>
                                <div className="step-circle">
                                    {step > 1 ? <i className="ph ph-check" /> : '1'}
                                </div>
                                <span className="step-label">Email</span>
                            </div>

                            <div className={'step-item ' + (step === 2 ? 'active' : step > 2 ? 'completed' : '')}>
                                <div className="step-circle">
                                    {step > 2 ? <i className="ph ph-check" /> : '2'}
                                </div>
                                <span className="step-label">Verify OTP</span>
                            </div>

                            <div className={'step-item ' + (step === 3 ? 'active' : '')}>
                                <div className="step-circle">
                                    3
                                </div>
                                <span className="step-label">New Password</span>
                            </div>
                        </div>
                    )}

                    {/* Global Error Banner */}
                    {globalError && (
                        <div className="global-alert error">
                            <i className="ph ph-warning-circle" style={{ fontSize: '20px', flexShrink: 0, marginTop: '2px' }} />
                            <div>{globalError}</div>
                        </div>
                    )}

                    {/* ── STEP 1: EMAIL SCREEN ──────────────────────────────── */}
                    {step === 1 && (
                        <div>
                            <div className="text-center mb-28">
                                <div style={{ width: '56px', height: '56px', background: '#e8f5e9', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                                    <i className="ph ph-lock-key-open" style={{ fontSize: '28px', color: '#2FA84F' }} />
                                </div>
                                <h3 className="text-24 font-bold text-gray-900 mb-8">Forgot Password?</h3>
                                <p className="text-gray-600 text-sm" style={{ lineHeight: '1.6' }}>
                                    Enter your registered email address below. We will verify your account and send a 6-digit OTP code to reset your password.
                                </p>
                            </div>

                            <form onSubmit={handleEmailSubmit} noValidate>
                                <div className="auth-input-group">
                                    <i className="ph ph-envelope-simple left-icon" />
                                    <input
                                        type="email"
                                        className="auth-input auth-input-no-right"
                                        placeholder="Enter your registered email"
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            setEmailError('');
                                            setGlobalError('');
                                        }}
                                        autoFocus
                                        autoComplete="email"
                                    />
                                    {emailError && (
                                        <span className="auth-error-msg">
                                            <i className="ph ph-warning" /> {emailError}
                                        </span>
                                    )}
                                </div>

                                <button type="submit" className="auth-submit-btn" disabled={isSubmitting}>
                                    {isSubmitting ? (
                                        <>
                                            <i className="ph ph-spinner animate-spin" style={{ fontSize: '18px' }} /> Checking Account...
                                        </>
                                    ) : (
                                        <>
                                            Send Verification Code <i className="ph ph-arrow-right" />
                                        </>
                                    )}
                                </button>
                            </form>

                            <div className="text-center mt-24 pt-20 border-t border-gray-100">
                                <Link to="/login" className="inline-flex items-center gap-6 text-sm text-gray-600 hover:text-main-600 font-medium transition-colors">
                                    <i className="ph ph-arrow-left" /> Back to Login
                                </Link>
                            </div>
                        </div>
                    )}

                    {/* ── STEP 2: OTP VERIFICATION SCREEN ───────────────────── */}
                    {step === 2 && (
                        <div>
                            <div className="text-center mb-24">
                                <div style={{ width: '56px', height: '56px', background: '#e8f5e9', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                                    <i className="ph ph-shield-check" style={{ fontSize: '28px', color: '#2FA84F' }} />
                                </div>
                                <h3 className="text-24 font-bold text-gray-900 mb-8">Enter Verification Code</h3>
                                <p className="text-gray-600 text-sm" style={{ lineHeight: '1.6' }}>
                                    We have sent a 6-digit security code to{' '}
                                    <strong className="text-gray-900">{email}</strong>
                                </p>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setStep(1);
                                        setGlobalError('');
                                        setOtpError('');
                                    }}
                                    className="text-xs text-main-600 hover:underline font-semibold mt-6 inline-flex items-center gap-4"
                                >
                                    <i className="ph ph-pencil-simple" /> Change Email Address
                                </button>
                            </div>

                            <form onSubmit={handleOtpSubmit}>
                                {/* 6 Individual Digit Boxes */}
                                <div className="otp-box-container" onPaste={handleOtpPaste}>
                                    {otp.map((digit, idx) => (
                                        <input
                                            key={idx}
                                            ref={(el) => (otpRefs.current[idx] = el)}
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            maxLength={1}
                                            value={digit}
                                            onChange={(e) => handleOtpChange(idx, e)}
                                            onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                                            className={'otp-single-box ' + (digit ? 'filled' : '')}
                                        />
                                    ))}
                                </div>

                                {otpError && (
                                    <div className="text-center mb-16">
                                        <span className="auth-error-msg justify-center">
                                            <i className="ph ph-warning" /> {otpError}
                                        </span>
                                    </div>
                                )}

                                {/* Expiry Notice & 1-Minute Resend Button */}
                                <div className="otp-meta-row">
                                    <div className="otp-badge">
                                        <i className="ph ph-clock text-amber-600" style={{ fontSize: '16px' }} />
                                        <span>Expires in <strong>10 minutes</strong></span>
                                    </div>

                                    <div>
                                        {resendSeconds > 0 ? (
                                            <span className="text-xs text-gray-500 font-medium inline-flex items-center gap-4">
                                                <i className="ph ph-hourglass-simple" />
                                                Resend code in <strong>{formatSeconds(resendSeconds)}</strong>
                                            </span>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={handleResendOtp}
                                                disabled={isResending}
                                                className="otp-resend-btn"
                                            >
                                                {isResending ? (
                                                    <>
                                                        <i className="ph ph-spinner animate-spin" /> Sending...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="ph ph-arrow-clockwise" /> Resend Code
                                                    </>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    className="auth-submit-btn mt-24"
                                    disabled={isSubmitting || otp.join('').length !== 6}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <i className="ph ph-spinner animate-spin" style={{ fontSize: '18px' }} /> Verifying Code...
                                        </>
                                    ) : (
                                        <>
                                            Verify Code & Continue <i className="ph ph-arrow-right" />
                                        </>
                                    )}
                                </button>
                            </form>

                            <div className="text-center mt-24 pt-20 border-t border-gray-100">
                                <button
                                    type="button"
                                    onClick={() => setStep(1)}
                                    className="inline-flex items-center gap-6 text-sm text-gray-600 hover:text-main-600 font-medium transition-colors"
                                >
                                    <i className="ph ph-arrow-left" /> Back to previous step
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ── STEP 3: NEW PASSWORD SCREEN ───────────────────────── */}
                    {step === 3 && (
                        <div>
                            <div className="text-center mb-28">
                                <div style={{ width: '56px', height: '56px', background: '#e8f5e9', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                                    <i className="ph ph-key" style={{ fontSize: '28px', color: '#2FA84F' }} />
                                </div>
                                <h3 className="text-24 font-bold text-gray-900 mb-8">Set New Password</h3>
                                <p className="text-gray-600 text-sm" style={{ lineHeight: '1.6' }}>
                                    Enter your new password below. Make sure it is secure and easy for you to remember.
                                </p>
                            </div>

                            <form onSubmit={handlePasswordSubmit}>
                                <div className="auth-input-group">
                                    <i className="ph ph-lock-key left-icon" />
                                    <input
                                        type={showNewPassword ? 'text' : 'password'}
                                        className="auth-input"
                                        placeholder="New Password (min. 6 characters)"
                                        value={passwords.newPassword}
                                        onChange={(e) => {
                                            setPasswords({ ...passwords, newPassword: e.target.value });
                                            setPasswordErrors({ ...passwordErrors, newPassword: '' });
                                        }}
                                        autoFocus
                                    />
                                    <i
                                        className={'ph ' + (showNewPassword ? 'ph-eye-slash' : 'ph-eye') + ' right-icon'}
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                    />
                                    {passwordErrors.newPassword && (
                                        <span className="auth-error-msg">
                                            <i className="ph ph-warning" /> {passwordErrors.newPassword}
                                        </span>
                                    )}
                                </div>

                                <div className="auth-input-group">
                                    <i className="ph ph-lock-key left-icon" />
                                    <input
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        className="auth-input"
                                        placeholder="Confirm New Password"
                                        value={passwords.confirmPassword}
                                        onChange={(e) => {
                                            setPasswords({ ...passwords, confirmPassword: e.target.value });
                                            setPasswordErrors({ ...passwordErrors, confirmPassword: '' });
                                        }}
                                    />
                                    <i
                                        className={'ph ' + (showConfirmPassword ? 'ph-eye-slash' : 'ph-eye') + ' right-icon'}
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    />
                                    {passwordErrors.confirmPassword && (
                                        <span className="auth-error-msg">
                                            <i className="ph ph-warning" /> {passwordErrors.confirmPassword}
                                        </span>
                                    )}
                                </div>

                                {/* Password requirements checklist */}
                                <ul className="password-reqs">
                                    <li style={{ color: passwords.newPassword.length >= 6 ? '#16a34a' : '#6b7280' }}>
                                        <i className={'ph ' + (passwords.newPassword.length >= 6 ? 'ph-check-circle-fill' : 'ph-circle')} />
                                        At least 6 characters long
                                    </li>
                                    <li style={{ color: passwords.newPassword && passwords.newPassword === passwords.confirmPassword ? '#16a34a' : '#6b7280' }}>
                                        <i className={'ph ' + (passwords.newPassword && passwords.newPassword === passwords.confirmPassword ? 'ph-check-circle-fill' : 'ph-circle')} />
                                        Passwords match
                                    </li>
                                </ul>

                                <button type="submit" className="auth-submit-btn" disabled={isSubmitting}>
                                    {isSubmitting ? (
                                        <>
                                            <i className="ph ph-spinner animate-spin" style={{ fontSize: '18px' }} /> Updating Password...
                                        </>
                                    ) : (
                                        <>
                                            Reset Password <i className="ph ph-check" />
                                        </>
                                    )}
                                </button>
                            </form>
                        </div>
                    )}

                    {/* ── STEP 4: SUCCESS STATE ─────────────────────────────── */}
                    {step === 4 && (
                        <div className="text-center py-16">
                            <div style={{ width: '72px', height: '72px', background: '#e8f5e9', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                                <i className="ph ph-check-circle text-main-600" style={{ fontSize: '44px' }} />
                            </div>
                            <h3 className="text-24 font-bold text-gray-900 mb-8">Password Reset Successful!</h3>
                            <p className="text-gray-600 text-sm mb-24" style={{ lineHeight: '1.6' }}>
                                Your password has been changed securely. You can now use your new password to sign in to your Healthy Homes account.
                            </p>
                            
                            <div className="mb-24 p-12 bg-green-50 rounded-lg text-sm text-main-700 font-medium inline-block">
                                Redirecting to Login in <strong>{redirectCountdown}</strong> seconds...
                            </div>

                            <Link to="/login" className="auth-submit-btn text-center">
                                Sign In Now <i className="ph ph-arrow-right" />
                            </Link>
                        </div>
                    )}

                </div>
            </div>
        </section>
    );
};

export default ForgotPasswordSection;
